/**
 * Clean Reverse Transpiler for Bi-Directional Synchronizer
 * Converts edited target code back to source language without comment banners or boilerplate pollution.
 */

// Helper to strip standard auto-generated headers and banner comments
export const stripGeneratedHeaders = (code) => {
  if (!code) return "";
  
  // 1. Remove multi-line comments
  let cleaned = code.replace(/\/\*[\s\S]*?\*\//g, "");

  // 2. Remove standard Practido banner lines line by line from top
  const lines = cleaned.split("\n");
  const filteredLines = [];
  let isHeaderZone = true;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      if (!isHeaderZone) filteredLines.push(line);
      continue;
    }

    const isBannerLine =
      trimmed.startsWith("// =") ||
      trimmed.startsWith("# =") ||
      trimmed.startsWith("// Generated") ||
      trimmed.startsWith("# Generated") ||
      trimmed.startsWith("// Source:") ||
      trimmed.startsWith("# Source:") ||
      trimmed.startsWith("// Target:") ||
      trimmed.startsWith("# Target:") ||
      trimmed.startsWith("// Practido") ||
      trimmed.startsWith("# Practido") ||
      trimmed.includes("Transpiled Python Execution") ||
      trimmed.includes("Practido Python to Arduino") ||
      trimmed.includes("Practido JS to Arduino Bridge") ||
      trimmed.includes("Arduino Hardware Initialized");

    if (isHeaderZone && isBannerLine) {
      continue; // Skip header lines at top of file
    }

    // Once we hit non-banner code, end the header zone
    isHeaderZone = false;

    // Filter out stray generated banner lines even if found later
    if (isBannerLine) {
      continue;
    }

    filteredLines.push(line);
  }

  return filteredLines.join("\n").trim();
};

/**
 * Reverse converts JavaScript code back to Python cleanly
 */
export const reverseJsToPython = (jsCode) => {
  const cleanCode = stripGeneratedHeaders(jsCode);
  if (!cleanCode) return "";

  const lines = cleanCode.split("\n");
  const pyLines = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) {
      pyLines.push("");
      continue;
    }

    // Skip pure brace lines
    if (trimmed === "}" || trimmed === "};") continue;

    // Convert console.log
    line = line.replace(/console\.log\(`(.*?)`\);?/g, (m, p1) => {
      const formatted = p1.replace(/\$\{(.*?)\}/g, "{$1}");
      return `print(f"${formatted}")`;
    });
    line = line.replace(/console\.log\((.*?)\);?/g, "print($1)");

    // Convert functions
    line = line.replace(/function\s+([a-zA-Z0-9_]+)\s*\((.*?)\)\s*\{?/g, "def $1($2):");

    // Convert for loops: for (let i = 0; i < 5; i++) -> for i in range(0, 5):
    line = line.replace(/for\s*\(\s*(?:let|var|const)?\s*([a-zA-Z0-9_]+)\s*=\s*(\d+)\s*;\s*\1\s*<\s*(\d+)\s*;\s*\1\+\+\s*\)\s*\{?/g, "for $1 in range($2, $3):");
    line = line.replace(/for\s*\(\s*(?:let|var|const)?\s*([a-zA-Z0-9_]+)\s*=\s*(\d+)\s*;\s*\1\s*<=\s*(\d+)\s*;\s*\1\+\+\s*\)\s*\{?/g, "for $1 in range($2, $3 + 1):");

    // Convert while loops
    line = line.replace(/while\s*\((.*?)\)\s*\{?/g, "while $1:");

    // Convert if / else if / else
    line = line.replace(/else\s+if\s*\((.*?)\)\s*\{?/g, "elif $1:");
    line = line.replace(/if\s*\((.*?)\)\s*\{?/g, "if $1:");
    line = line.replace(/else\s*\{?/g, "else:");

    // Convert variable declarations: let x = 10 -> x = 10
    line = line.replace(/^(?:\s*)(?:let|const|var)\s+([a-zA-Z_]\w*\s*=)/g, "$1");

    // Convert boolean / null keywords
    line = line.replace(/\btrue\b/g, "True").replace(/\bfalse\b/g, "False").replace(/\bnull\b/g, "None");

    // Convert JS single line comments (only if not inside string)
    line = line.replace(/\/\/(.*)$/, "#$1");

    // Remove trailing semicolons
    line = line.replace(/;(?=\s*$)/, "");

    // Remove stray braces at end of line
    line = line.replace(/\s*\{\s*$/, "");

    pyLines.push(line);
  }

  return pyLines.join("\n").trim();
};

/**
 * Reverse converts Python code back to JavaScript cleanly
 */
export const reversePythonToJs = (pyCode) => {
  const cleanCode = stripGeneratedHeaders(pyCode);
  if (!cleanCode) return "";

  const lines = cleanCode.split("\n");
  const jsLines = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      jsLines.push("");
      continue;
    }

    // Convert print statements
    line = line.replace(/print\(f(["'])(.*?)\1\)/g, (m, quote, content) => {
      const formatted = content.replace(/\{(.*?)\}/g, "${$1}");
      return `console.log(\`${formatted}\`);`;
    });
    line = line.replace(/print\((.*?)\)/g, "console.log($1);");

    // Convert def -> function
    line = line.replace(/def\s+([a-zA-Z0-9_]+)\s*\((.*?)\):/g, "function $1($2) {");

    // Convert for loops: for i in range(5): or for i in range(1, 5):
    line = line.replace(/for\s+([a-zA-Z0-9_]+)\s+in\s+range\((\d+)\):/g, "for (let $1 = 0; $1 < $2; $1++) {");
    line = line.replace(/for\s+([a-zA-Z0-9_]+)\s+in\s+range\((\d+),\s*(\d+)\):/g, "for (let $1 = $2; $1 < $3; $1++) {");

    // Convert while loops
    line = line.replace(/while\s+(.*?):/g, "while ($1) {");

    // Convert if / elif / else
    line = line.replace(/elif\s+(.*?):/g, "} else if ($1) {");
    line = line.replace(/if\s+(.*?):/g, "if ($1) {");
    line = line.replace(/else:/g, "} else {");

    // Convert boolean / None keywords
    line = line.replace(/\bTrue\b/g, "true").replace(/\bFalse\b/g, "false").replace(/\bNone\b/g, "null");

    // Convert Python comments to JS comments
    line = line.replace(/#(.*)$/, "//$1");

    jsLines.push(line);
  }

  return jsLines.join("\n").trim();
};

/**
 * Clean reverse conversion from any target language to source language
 */
export const reverseTranspileCode = (editedCode, fromLang, toLang) => {
  if (!editedCode || !editedCode.trim()) return "";
  if (fromLang === toLang) return stripGeneratedHeaders(editedCode).trim();

  // Strip boilerplate wrappers from Arduino / C++ if present
  let cleanBody = stripGeneratedHeaders(editedCode);

  // Strip C++ / Arduino main/setup wrappers to get inner body statements
  if (fromLang === "cpp" || fromLang === "arduino") {
    cleanBody = cleanBody
      .replace(/#include\s*<.*?>/g, "")
      .replace(/using\s+namespace\s+std;/g, "")
      .replace(/Serial\.begin\(\d+\);?/g, "")
      .replace(/void\s+setup\s*\(\)\s*\{([\s\S]*?)\}\s*void\s+loop\s*\(\)\s*\{[\s\S]*?\}/g, "$1")
      .replace(/int\s+main\s*\(\)\s*\{([\s\S]*?)return\s+0;\s*\}/g, "$1")
      .replace(/Serial\.println\((.*?)\);?/g, "console.log($1);")
      .replace(/std::cout\s*<<\s*(.*?)\s*<<\s*std::endl;?/g, "console.log($1);")
      .replace(/std::cout\s*<<\s*(.*?);?/g, "console.log($1);")
      .replace(/auto\s+|int\s+|float\s+|double\s+|String\s+|bool\s+/g, "let ")
      .trim();

    if (toLang === "javascript") {
      return cleanBody;
    } else if (toLang === "python") {
      return reverseJsToPython(cleanBody);
    }
  }

  if (fromLang === "javascript" && toLang === "python") {
    return reverseJsToPython(cleanBody);
  }

  if (fromLang === "python" && toLang === "javascript") {
    return reversePythonToJs(cleanBody);
  }

  return cleanBody;
};
